import { useState } from 'react';
import { fieldClass, formatGrouped, parseNumber, toEnDigits } from '../utils/form';
import { cn } from '../utils/cn';

type Props = {
  value: string;
  onChange: (digits: string) => void;
  label?: string;
  hint?: string;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  /** Show thousand separators when not focused (default true) */
  grouped?: boolean;
  disabled?: boolean;
  /** Allow leading minus (inventory adjustments) */
  allowNegative?: boolean;
};

/**
 * Numeric field that accepts Persian & English digits interchangeably.
 * Parent keeps raw digit string; display uses 1,234,567 grouping.
 */
export function NumberField({
  value,
  onChange,
  label,
  hint,
  className,
  inputClassName,
  placeholder,
  grouped = true,
  disabled,
  allowNegative = false,
}: Props) {
  const [focused, setFocused] = useState(false);

  const normalize = (raw: string) => {
    let s = toEnDigits(raw).replace(/,/g, '').replace(/٬/g, '');
    const neg = allowNegative && s.trim().startsWith('-');
    s = s.replace(/[^0-9]/g, '');
    if (neg && s) return `-${s}`;
    if (neg && !s) return '-';
    return s;
  };

  const digits = normalize(value);
  const display =
    focused || !grouped || digits === '' || digits === '-'
      ? digits
      : formatGrouped(parseNumber(digits));

  return (
    <div className={className}>
      {label ? <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label> : null}
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        disabled={disabled}
        className={cn(fieldClass, 'text-left tabular-nums', inputClassName)}
        value={display}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(normalize(e.target.value))}
      />
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
